import styled, { css } from 'styled-components';
import { Link as RRDLink } from '@/providers/RouterShim';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';
import { Body as InlineMediaContentBody } from '../../structures/InlineMediaContent/wrapper';

import { Foldout, Wrapper as FoldoutWrapper } from '../Foldout/wrapper';
import {
  Wrapper as MenuFoldout,
  openButtonSize as menuFoldoutOpenButtonSize,
} from '../MenuFoldout/wrapper';

export const TableOverflow = styled.div`
  ${(props) => props.theme.breakpoint.Sm} {
    display: flex;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

export const Table = styled.div`
  ${(props) => props.theme.breakpoint.Sm} {
    display: table;
    width: 100%;
    border-collapse: collapse;
  }
`;

export const Thead = styled.div`
  ${(props) => props.theme.breakpoint.LtSm} {
    display: none;
  }

  ${(props) => props.theme.breakpoint.Sm} {
    display: table-header-group;
  }
`;

export const Tbody = styled.div`
  ${(props) => props.theme.breakpoint.Sm} {
    display: table-row-group;
    border-top: 1px solid var(--c-border-main);
  }
`;

export const Tr = styled.div`
  ${(props) => props.theme.breakpoint.LtSm} {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: ${size(2)} var(--parent-gutter, ${size(2)});

    & + & {
      border-top: 1px solid var(--c-border-main);
    }

    &:first-child {
      border-top: 1px solid var(--c-border-main);
    }
  }

  ${(props) => props.theme.breakpoint.Sm} {
    display: table-row;
  }
`;

// Styling shared between cells (Th & Td)
const CellBase = styled.div`
  ${(props) => props.theme.breakpoint.Sm} {
    display: table-cell;
    vertical-align: middle;
    padding: ${size(1.5)};

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

// Styling shared between Td:s (Td & TdFoldout) based on CellBase
const TdBase = styled(CellBase)`
  ${(props) => props.theme.breakpoint.Sm} {
    border-bottom: 1px solid var(--c-border-main);
    background-color: var(--c-bg-main);
  }
`;

interface ThProps {
  $isMinimal?: boolean;
}

export const Th = styled(CellBase)<ThProps>`
  ${(props) => props.theme.breakpoint.Sm} {
    padding-top: 0;
    text-align: left;
    font-size: ${(props) => props.theme.font.size.xsmall};
    color: var(--c-text-muted);
    white-space: nowrap;

    ${({ $isMinimal }) =>
      $isMinimal &&
      css`
        width: 1px;
      `}
  }
`;

interface TdProps {
  $noWrap?: boolean;
  $actionHitbox?: boolean;
  $isMinimal?: boolean;
}

export const Td = styled(TdBase)<TdProps>`
  ${(props) => props.theme.breakpoint.LtSm} {
    width: 100%;

    &[data-heading] {
      &::before {
        content: attr(data-heading);
        display: block;
        color: var(--c-text-muted);
        font-size: ${(props) => props.theme.font.size.small};
        margin-bottom: ${size(0.5)};
      }
    }

    & + & {
      margin-top: ${size(2)};
    }

    &:first-child {
      order: -2;
      flex: 1 1 auto;
      width: calc(100% - (${size(menuFoldoutOpenButtonSize)} + ${size(1)}));
    }

    ${InlineMediaContentBody} {
      > * {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  ${(props) => props.theme.breakpoint.Sm} {
    ${({ $noWrap }) =>
      $noWrap &&
      css`
        white-space: nowrap;
      `}

    ${({ $actionHitbox }) =>
      $actionHitbox &&
      css`
        position: relative;
      `}

    ${({ $isMinimal }) =>
      $isMinimal &&
      css`
        width: 1px;
      `}
  }
`;

export const TdContentGap = styled.div`
  display: flex;
  align-items: center;
  gap: ${size(0.75)};
`;

export const TdFoldout = styled(TdBase)`
  ${(props) => props.theme.breakpoint.LtSm} {
    order: -1;
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    width: calc(${size(menuFoldoutOpenButtonSize)} + ${size(1)});
  }

  ${(props) => props.theme.breakpoint.Sm} {
    width: 1px;

    // Unique styling to MenuFoldout component placed inside this node

    ${MenuFoldout} {
      position: static; // Required to pop out of overflow: auto
    }

    ${FoldoutWrapper} {
      position: static; // Required to pop out of overflow: auto
    }

    ${Foldout} {
      top: auto;
      right: calc(var(--parent-gutter, 0) + ${size(2)});
      padding-bottom: ${size(5)}; // Add spacing to viewport bottom if the Foldout extends the viewport
    }
  }
`;

interface TdGroupProps {
  $hidden?: boolean; // Used to hide group in small breakpoint
}

export const TdGroup = styled.div<TdGroupProps>`
  ${(props) => props.theme.breakpoint.LtSm} {
    width: 100%;
    font-size: ${(props) => props.theme.font.size.small};

    ${Td} + & {
      margin-top: ${size(2)};
      padding-top: ${size(2)};
      border-top: 1px solid var(--c-border-main);
    }

    ${Td} {
      &:first-child {
        order: unset;
        flex: none;
        width: auto;
        padding-right: 0;
      }

      &[data-heading] {
        display: flex;
        align-items: flex-start;
        justify-content: flex-end;
        text-align: right;

        &::before {
          flex-shrink: 0;
          margin-top: calc(
            (${(props) => props.theme.font.size.small} - ${(props) => props.theme.font.size.xsmall}) /
              2
          );
          margin-bottom: 0; // Reset margin from Td heading outside TdGroup
          margin-right: auto;
          padding-right: ${size(1.5)};
          text-align: left;
        }
      }

      & + ${Td} {
        margin-top: ${size(1.5)};
      }
    }

    // Add margin to Td placed below TdGroup
    & + ${Td} {
      margin-top: ${size(2)};
    }

    ${({ $hidden }) =>
      $hidden &&
      css`
        display: none;
      `}
  }

  ${(props) => props.theme.breakpoint.Sm} {
    display: contents;

    &:not(:first-child) {
      ${Td} {
        &:first-child {
          padding-left: ${size(2)};
        }
      }
    }

    &:not(:last-child) {
      ${Td} {
        &:last-child {
          padding-right: ${size(2)};
        }
      }
    }
  }
`;

const CellActionBaseStyle = css`
  --_font-size: ${(props) => props.theme.font.size.regular};

  font-weight: var(--font-weight-bold);
  font-size: var(--_font-size);
  max-width: max-content;
  color: currentColor;
  cursor: pointer;

  &:not([disabled]):hover {
    text-decoration: underline;
  }
`;

interface CellActionProps {
  $size?: 'inherit';
}

export const CellAction = styled.div.attrs(() => ({
  role: 'button',
  tabIndex: 0,
}))<CellActionProps>`
  ${CellActionBaseStyle};

  ${({ $size }) =>
    $size === 'inherit'
      ? css`
          font-size: 1em;
        `
      : undefined}
`;

interface CellRouterLinkProps {
  $hoverGhost?: boolean;
}

export const CellRouterLink = styled(RRDLink)<CellRouterLinkProps>`
  ${CellActionBaseStyle};

  text-decoration: none;

  ${({ $hoverGhost }) =>
    $hoverGhost &&
    css`
      ${(props) => props.theme.breakpoint.Sm} {
        &::before {
          content: '';
          position: absolute;
          inset: 0;
        }
      }
    `}
`;

export const CellToggleTdGroup = styled(ButtonReset)`
  display: flex;
  align-items: center;
  gap: ${size(0.5)};
  color: currentColor;
  font-size: ${(props) => props.theme.font.size.xsmall};
  text-decoration: underline;

  ${(props) => props.theme.breakpoint.Sm} {
    display: none;
  }
`;

export const CellIndentationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${size(2)};
`;

export const CellIndentationGroup = styled.div`
  display: flex;
  align-items: stretch;
  align-self: stretch;
  margin-block: ${size(-1.5)};

  ${(props) => props.theme.breakpoint.LtSm} {
    margin-block: ${size(-2)};
    margin-left: ${size(-2)};
  }
`;

export const CellIndentation = styled.div.attrs(() => ({
  'aria-hidden': 'true',
}))`
  width: ${size(1)};
  background-color: var(--c-accent-bg);
`;

interface TableWrapperProps {
  $mainListing?: boolean; // Used when table is used as listing on a main page
}

export const TableWrapper = styled.div<TableWrapperProps>`
  // Change these custom properties in other parent components to adjust the overflow gutter
  --parent-gutter: 0px;
  --parent-gutter-negative: 0px;

  margin: 0 var(--parent-gutter-negative);

  ${(props) => props.theme.breakpoint.Sm} {
    position: relative; // Required for Foldout to pop out of overflow: auto

    ${TableOverflow} {
      padding-left: var(--parent-gutter);
      padding-right: var(--parent-gutter);

      /*
      &:after {
        content: '';
        display: block;
        flex-shrink: 0;
        width: var(--parent-gutter);
        margin-right: var(--parent-gutter-negative);
      }
      */
    }
  }

  // Styling applied when used as listing on a main page
  ${({ $mainListing }) =>
    $mainListing
      ? css`
          --parent-gutter: var(--main-gutter);
          --parent-gutter-negative: var(--main-gutter-negative);
        `
      : css`
          ${MenuFoldout} {
            margin-right: 0;
          }

          ${(props) => props.theme.breakpoint.LtSm} {
            ${Tr} {
              &:last-child {
                padding-bottom: 0;
              }
            }
          }
        `}
`;
